<template>
  <v-card
    @click="handleCardClick"
    :class="getCardClass()"
    :title="
      shouldShowStoryBody
        ? false
        : 'Essa retranca foi protegida pelo autor e somente pode ser visualizada no iNews com a senha.'
    "
  >
    <v-card-title class="card-header">
      <div class="slot header">
        <div class="path">
          <v-icon left>folder_special</v-icon>
          <v-chip v-for="(tag, index) in pathToList" outlined :key="index"
            >{{ tag }} /</v-chip
          >
        </div>

        <div class="tags">
          <div class="protected" v-if="!shouldShowStoryBody">
            <v-icon>lock</v-icon>
          </div>
          <div
            class="contacts"
            v-if="story.data.has_contacts && shouldShowStoryBody"
          >
            <v-icon>mdi-account-circle-outline</v-icon>
          </div>

          <div class="similar" v-if="shouldShowBadge && shouldShowStoryBody">
            <v-icon>mdi-lightbulb-outline</v-icon>
          </div>

          <div class="place">{{ story.data.place }}</div>
          <div class="program" v-if="story.data.program">
            {{ story.data.program }}
          </div>
        </div>
      </div>

      <div class="slot">
        <div class="title" v-html="story.preview.title"></div>
      </div>
    </v-card-title>

    <v-card-text
      v-if="shouldShowStoryBody"
      class="card-text"
      v-html="escapeHTML(story.preview.body)"
    ></v-card-text>

    <div class="card-contacts" v-if="shouldShowContactChips">
      <v-chip-group>
        <v-menu
          open-on-hover
          bottom
          transition="slide-y-transition"
          v-for="(contact_kvs, index) in story.data.contacts_kvs.slice(
            0,
            CONTACT_DISPLAY_LIMIT,
          )"
          :key="index"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip class="mb-1" label v-bind="attrs" v-on="on">
              <v-icon>account_box</v-icon>
              <div class="contacts-text">
                {{ getContactDisplayString(contact_kvs) }}
              </div>
            </v-chip>
          </template>

          <div class="dropdown">
            <div class="dropdown-header">Informações do contato</div>

            <div class="dropdown-content">
              <v-list>
                <v-list-item ripple>
                  <div class="key">
                    {{ getContactTooltipDisplayString(contact_kvs).title }}
                  </div>
                  <div class="value">
                    {{
                      getContactTooltipDisplayString(contact_kvs).description
                    }}
                  </div>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-menu>
        <v-menu
          open-on-hover
          bottom
          transition="slide-y-transition"
          v-if="story.data.contacts_kvs.length > CONTACT_DISPLAY_LIMIT"
          :key="index"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip class="mb-1" label v-bind="attrs" v-on="on">
              <v-icon>account_box</v-icon>
              <div class="contacts-text">
                {{ story.data.contacts_kvs.length - CONTACT_DISPLAY_LIMIT }}
              </div>
            </v-chip>
          </template>

          <div class="dropdown">
            <div class="dropdown-header">Informações do contato</div>

            <div class="dropdown-content">
              <v-list>
                <v-list-item ripple>
                  <div class="key"></div>
                  <div class="value">
                    Entre na retranca para ver todos os contatos cadastrados.
                  </div>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-menu>
      </v-chip-group>
    </div>

    <v-card-actions class="card-actions">
      <v-chip outlined>
        <v-icon left>date_range</v-icon>
        {{ displayedStoryDate }}
      </v-chip>
      <v-chip outlined>
        <v-icon left>access_time</v-icon>
        {{ displayedStoryTime }}
      </v-chip>
      <v-chip outlined class="name">
        <v-icon left>person</v-icon>
        {{ displayedAuthor }}
      </v-chip>
      <v-btn icon @click.stop="selectStory(story)">
        <v-icon>keyboard_arrow_right</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { timeFormat, dateFormat } from '@/utils/date.js';
import { countWords, escapeHTML } from '@/utils/text.js';
import { toLocalTime } from '@/utils/date.js';
import { getEnvVar } from '@/utils/getEnvVar';
import { mapState } from 'vuex';

export default {
  props: {
    story: Object,
    isContactMode: Boolean,
  },

  data() {
    return {
      CONTACT_DISPLAY_LIMIT: 2,
      shouldShowBadge: false,
    };
  },
  mounted() {
    this.shouldShowBadge = this.checkConflictsBadge();
  },
  watch: {
    'story.data.conflicts': function() {
      this.shouldShowBadge = this.checkConflictsBadge();
    },
  },
  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
    }),

    pathToList() {
      const path = this.story.data.path;
      return path ? path.split('.') : [];
    },

    displayedStoryDate() {
      return this.creationDate(
        toLocalTime(this.story.data.processing_date) ||
          toLocalTime(this.story.data.modified_date) ||
          toLocalTime(this.story.data.creation_date) ||
          toLocalTime(this.story.data.ingest_date),
      );
    },

    displayedStoryTime() {
      return this.creationTime(
        toLocalTime(this.story.data.processing_date) ||
          toLocalTime(this.story.data.modified_date) ||
          toLocalTime(this.story.data.creation_date) ||
          toLocalTime(this.story.data.ingest_date),
      );
    },

    displayedAuthor() {
      return this.story.data.modified_by || this.story.data.author;
    },
    shouldShowContactChips() {
      return this.isContactMode && this.hasContacts;
    },

    contactsKeyValues() {
      return this.story.data.contacts_kvs || [];
    },

    hasContacts() {
      return this.contactsKeyValues.length > 0;
    },

    shouldShowStoryBody() {
      // Only shows body if the story isn't protected, or if it is protected and the authenticated user is either the author or has modified the story
      if (!('is_protected' in this.story.data)) return true;

      return (
        !this.story.data.is_protected ||
        (this.story.data.is_protected &&
          (this.story.data.modified_by === this.currentUser.username ||
            this.story.data.author === this.currentUser.username))
      );
    },

    resizeCardByText() {
      if (this.isContactMode && this.hasContacts) {
        return this.contactsKeyValues.length === 1
          ? 'single-contact'
          : 'multiple-contacts';
      } else {
        const size = escapeHTML(this.story.preview.body).length;
        return this.getCardTextClass(size);
      }
    },
  },

  methods: {
    escapeHTML,
    checkConflictsBadge() {
      return this.story.data.conflicts > 0 && this.shouldSearchConflicts();
    },
    handleCardClick() {
      if (this.shouldShowStoryBody) this.$emit('selectStory', this.story);
    },
    shouldSearchConflicts() {
      return (
        countWords(escapeHTML(this.story.data.body)) >
        parseInt(getEnvVar('VUE_APP_MIN_WORDS_CONFLICT'))
      );
    },
    creationDate(date) {
      return date ? dateFormat(date) : '---';
    },

    creationTime(date) {
      return date ? timeFormat(date) : '---';
    },

    getContactDisplayString(contact) {
      const contactValues = [];
      let name = null;
      contact.forEach(kv => {
        if (kv.key === 'nome') {
          name = kv.value;
          return;
        }
        contactValues.push(kv.value);
      });
      if (name) {
        contactValues.unshift(name);
      }
      return contactValues.join(' - ');
    },

    getContactTooltipDisplayString(contact) {
      return {
        title: contact[0].key,
        description: contact[0].value,
      };
    },
    getCardClass() {
      return (
        this.resizeCardByText +
        (!this.shouldShowStoryBody ? ' protected-card' : '')
      );
    },
    getCardTextClass(size) {
      if (size === 0 || !this.shouldShowStoryBody) return 'empty-text';
      if (size < 150) return 'card-text-smaller';
      if (size < 400) return 'card-text-small';
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/fixes.scss';
@import '@/assets/scss/inputs.scss';

.title,
.card-text,
.headline {
  ::v-deep hlt {
    background-color: $color-highlight;
    font-weight: 600;
  }
}

.v-card {
  padding: 12px 16px 16px;
  box-sizing: border-box;
  background-color: $color-white;
  box-shadow: 0 4px 8px 0 rgba($color-black, 0.04);
  border-radius: 4px;
  transition: box-shadow 0.4s ease-in-out;
  animation: fadeIn 0.8s ease-in-out;

  @include for-size(desktop-down) {
    border-radius: 8px;
    height: auto !important;
    margin: 0 0 16px 0;
    padding: 8px 16px;
  }

  // Definições de altura no grid
  grid-row-end: span 8;

  &.empty-text {
    grid-row-end: span 4;

    .card-text {
      display: none;
    }
  }

  &.card-text-small {
    grid-row-end: span 6;

    .card-text {
      -webkit-line-clamp: 4;
    }
  }

  &.card-text-smaller {
    grid-row-end: span 5;

    .card-text {
      -webkit-line-clamp: 2;
    }
  }

  &.single-contact {
    .card-text {
      -webkit-line-clamp: 6;
    }
  }

  &.multiple-contacts {
    .card-text {
      -webkit-line-clamp: 4;
    }
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0 8px 24px 0 rgba($color-black, 0.16);
  }

  .card-header {
    padding: 0;
    font-size: 1em !important;

    .slot {
      display: flex;
      justify-content: space-around;
      align-items: center;
      flex: 0 0 100%;
      min-width: 0;
      max-height: 32px;

      .path {
        white-space: nowrap;
        overflow: hidden;
        font-size: 1.4em !important;
        flex-basis: 50%;
        flex-grow: 2;
        line-height: 1em;

        i {
          font-size: 0.88em;
          margin: -2px 4px 0 0;
          padding: 0;
          color: $color-black-16;
        }

        .v-chip {
          display: inline-block;
          padding: 4px 0;
          margin: 0 2px 0 0;
          pointer-events: none;
          border: none;
          font-family: 'globotipo' !important;
          font-weight: 500 !important;
          font-size: 0.52em;
          letter-spacing: 0;
          color: $color-black-40;
        }
      }

      .tags {
        position: relative;
        display: flex;
        justify-content: flex-end;
        flex: 1;
        flex-wrap: nowrap;
        max-height: inherit;
        flex-grow: 3;

        // CONTACTS + SIMILAR NOTIFY
        .contacts,
        .protected,
        .similar {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          margin-left: 4px;
          border-radius: 4px;

          .v-icon {
            font-size: 1em;
          }
        }

        .protected {
          background-color: rgba($color-protected, 0.16);
          .v-icon {
            color: $color-protected;
          }
        }

        .contacts {
          background-color: rgba($color-contact, 0.16);

          .v-icon {
            color: $color-contact;
          }
        }

        .similar {
          background-color: rgba($color-similar, 0.16);

          .v-icon {
            color: $color-similar;
          }
        }

        .place,
        .program {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0;
          box-sizing: border-box;
          margin-left: 4px;
          height: 24px;
          width: 24px;
          font-size: 0.64em;
          font-family: 'globotipo' !important;
          font-weight: 600 !important;
          text-align: center;
          color: $color-black-64;
          border-radius: 4px;
          background-color: $color-black-08;
        }

        .badge {
          position: relative;
          margin-right: 6px;
          font-size: 1em;
          color: $color-secondary;
          animation: flash 4.8s ease-in-out infinite;
        }
      }

      .title {
        flex: 1;
        font-family: 'globotipo' !important;
        font-weight: 600 !important;
        font-size: 1em !important;
        color: $color-black-80;
        text-transform: uppercase;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }
  }

  .card-text {
    font-size: 0.8em;
    font-style: italic;
    line-height: 1.4em;
    text-align: left;
    padding: 0;
    margin: 8px 0 0 0;
    color: $color-black-56;
    display: -webkit-box;
    -webkit-line-clamp: 8;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-contacts {
    margin: 16px 0 0 0;

    ::v-deep .v-slide-group__content {
      overflow: hidden;
    }

    ::v-deep .v-slide-group__content {
      flex: auto;
      flex-wrap: wrap;
    }

    .v-chip {
      padding: 0 8px !important;
      background-color: rgba($color-contact, 0.16) !important;

      .v-icon {
        font-size: 1.2em !important;
        color: $color-contact;
        margin: 0 8px 0 0;
      }

      .contacts-text {
        font-family: 'globotipo' !important;
        font-weight: 500 !important;
        font-size: 0.88em !important;
        color: $color-contact;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .second-contact {
      max-width: calc(100% - 44px);
    }
  }

  .card-actions {
    width: calc(100% - 32px);
    position: absolute;
    bottom: 4px;
    padding: 0;

    @include for-size(desktop-down) {
      position: relative;
      width: 100%;
      height: 24px;
      margin: 16px 0 0 0;
    }

    .v-btn {
      box-shadow: none;
      margin: 0 -8px 0 0;
      color: $color-black-16;
    }

    .v-chip {
      margin: 0 16px 0 -4px;
      padding: 0 8px;
      background-color: transparent;
      border: none;
      border-radius: 2px;
      font-family: 'globotipo' !important;
      font-weight: 500 !important;
      font-size: 0.8em;
      color: $color-black-40;
      pointer-events: none;

      &.name {
        min-width: 0;
        flex-grow: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      i {
        font-size: 1.4em;
        color: $color-black-16;
        margin-right: 4px;
        margin-top: -2px;
      }
    }
  }
}

.protected-card {
  background-color: $color-black-04;
}
</style>
